import React, { useState, useEffect } from 'react';

export const ProductModal = ({ show, onClose, onSubmit, isEdit, currentItem, brands }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        specifications: [],
        brand_id: '',
        category: '',
        price: '',
        images: []
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isEdit && currentItem) {
            setFormData({
                title: currentItem.title,
                description: currentItem.description,
                specifications: Object.entries(currentItem.specifications || {}).map(([key, value]) => ({ key, value: String(value) })),
                brand_id: currentItem.brand_id,
                category: currentItem.category,
                price: currentItem.price,
                images: []
            });
        } else {
            setFormData({
                title: '',
                description: '',
                specifications: [],
                brand_id: '',
                category: '',
                price: '',
                images: []
            });
        }
    }, [isEdit, currentItem]);

    const validate = () => {
        const newErrors = {};
        if (!formData.title) newErrors.title = 'Required';
        if (!formData.category) newErrors.category = 'Required';
        if (!formData.price || isNaN(formData.price)) newErrors.price = 'Valid price required';
        if (!formData.brand_id) newErrors.brand_id = 'Required';
        if (formData.specifications.length === 0) newErrors.specifications = 'At least one specification required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            const formDataToSubmit = new FormData();
            formDataToSubmit.append('title', formData.title);
            formDataToSubmit.append('description', formData.description);
            formDataToSubmit.append('specifications', JSON.stringify(Object.fromEntries(formData.specifications.map(spec => [spec.key, spec.value]))));
            formDataToSubmit.append('brand_id', formData.brand_id);
            formDataToSubmit.append('category', formData.category);
            formDataToSubmit.append('price', formData.price);

            formData.images.forEach((file, index) => {
                formDataToSubmit.append(`images[${index}]`, file);
            });

            if (isEdit && currentItem) {
                formDataToSubmit.append('id', currentItem.id);
            }

            onSubmit(formDataToSubmit);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSpecChange = (index, field, value) => {
        const newSpecs = [...formData.specifications];
        newSpecs[index][field] = value;
        setFormData({ ...formData, specifications: newSpecs });
    };

    const addSpecification = () => {
        setFormData({ ...formData, specifications: [...formData.specifications, { key: '', value: '' }] });
    };

    const removeSpecification = (index) => {
        const newSpecs = formData.specifications.filter((_, i) => i !== index);
        setFormData({ ...formData, specifications: newSpecs });
    };

    const handleImageChange = (e) => {
        setFormData({ ...formData, images: Array.from(e.target.files) });
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
            <div className="bg-white p-4 sm:p-6 rounded-lg max-w-sm sm:max-w-md md:max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <h2 className="text-lg sm:text-xl font-bold mb-4">{isEdit ? 'Edit Product' : 'Add Product'}</h2>
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                        <input name="title" value={formData.title} onChange={handleChange} placeholder="Title" className="w-full p-2.5 sm:p-3 border rounded text-sm sm:text-base" />
                        {errors.title && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.title}</p>}
                    </div>
                    <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Description" className="w-full p-2.5 sm:p-3 border rounded text-sm sm:text-base" rows={3} />
                    <div>
                        <select name="brand_id" value={formData.brand_id} onChange={handleChange} className="w-full p-2.5 sm:p-3 border rounded text-sm sm:text-base">
                            <option value="">Select Brand</option>
                            {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                        {errors.brand_id && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.brand_id}</p>}
                    </div>
                    <div>
                        <input name="category" value={formData.category} onChange={handleChange} placeholder="Category" className="w-full p-2.5 sm:p-3 border rounded text-sm sm:text-base" />
                        {errors.category && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.category}</p>}
                    </div>
                    <div>
                        <input name="price" type="number" value={formData.price} onChange={handleChange} placeholder="Price" className="w-full p-2.5 sm:p-3 border rounded text-sm sm:text-base" step="0.01" />
                        {errors.price && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.price}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1.5">Specifications</label>
                        {formData.specifications.map((spec, index) => (
                            <div key={index} className="flex flex-col sm:flex-row gap-2 mb-2">
                                <input
                                    type="text"
                                    placeholder="Key"
                                    value={spec.key}
                                    onChange={(e) => handleSpecChange(index, 'key', e.target.value)}
                                    className="flex-1 p-2 border rounded text-sm"
                                />
                                <input
                                    type="text"
                                    placeholder="Value"
                                    value={spec.value}
                                    onChange={(e) => handleSpecChange(index, 'value', e.target.value)}
                                    className="flex-1 p-2 border rounded text-sm"
                                />
                                <button
                                    type="button"
                                    onClick={() => removeSpecification(index)}
                                    className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded text-sm w-full sm:w-auto"
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                        {errors.specifications && <p className="text-red-500 text-xs sm:text-sm">{errors.specifications}</p>}
                        <button
                            type="button"
                            onClick={addSpecification}
                            className="text-blue-500 hover:text-blue-600 underline text-sm mt-1"
                        >
                            Add Specification
                        </button>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1.5">Images</label>
                        <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleImageChange}
                            className="w-full p-2 border rounded text-sm"
                        />
                    </div>

                    <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2.5 bg-gray-300 hover:bg-gray-400 rounded text-sm sm:text-base w-full sm:w-auto">Cancel</button>
                        <button type="submit" className="px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm sm:text-base w-full sm:w-auto">{isEdit ? 'Update' : 'Add'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};